import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Menu, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { Course, Section } from "@/lib/types";
import type { Json } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import MarkdownIt from "markdown-it";
import { useTranslation } from "react-i18next";

// Configure markdown-it
const md = new MarkdownIt({ html: true });

// Function to add IDs to headers in HTML content
const addHeaderIds = (html: string, currentSectionId?: string) => {
  return html
    .replace(/<h2>(.*?)<\/h2>/g, (match, title) => {
      const id = currentSectionId
        ? generateSubSectionId(currentSectionId, title)
        : generateId(title);
      return `<h2 id="${id}">${title}</h2>`;
    })
    .replace(/<h1>(.*?)<\/h1>/g, (match, title) => {
      const id = generateId(title);
      return `<h1 id="${id}">${title}</h1>`;
    });
};

interface SinglePageCourseProps {
  course: Course;
  hasAccess: boolean;
  userId?: string;
  showHeader?: boolean;
}

interface SubSection {
  id: string;
  title: string;
  sectionId: string;
}

// Utility functions
const createLocalStorageKey = (courseSlug: string, userId: string) =>
  `course_progress_${courseSlug}_${userId}`;

const calculateProgress = (completedCount: number, totalCount: number) =>
  Math.round((completedCount / Math.max(totalCount, 1)) * 100);

// Type guard function to safely convert JSON to string array
const jsonStringArrayToStringArray = (jsonArray: Json | null): string[] => {
  if (!jsonArray) return [];

  if (Array.isArray(jsonArray)) {
    return jsonArray.filter((item): item is string => typeof item === 'string');
  }

  return [];
};

// Consistent ID generation function
const generateId = (text: string) => {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
};

// Generate subsection ID
const generateSubSectionId = (sectionId: string, subSectionTitle: string) => {
  return `${sectionId}-${generateId(subSectionTitle)}`;
};

// Simple scroll to element function
const scrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    const navbarHeight = 80; // Approximate height of the fixed navbar
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
    // Update URL hash without triggering navigation
    window.history.pushState(null, "", `#${elementId}`);
  }
};

// Progress management hook
function useProgressManagement(userId: string | undefined, course: Course) {
  const [completedSections, setCompletedSections] = useState<Set<string>>(
    new Set(),
  );

  const loadProgress = useCallback(async () => {
    if (!userId) return;

    try {
      const localStorageKey = createLocalStorageKey(course.slug, userId);
      const savedProgress = localStorage.getItem(localStorageKey);

      if (savedProgress) {
        const parsed = JSON.parse(savedProgress);
        const localStorageSections = jsonStringArrayToStringArray(parsed.completedSections || []);
        setCompletedSections(new Set(localStorageSections));
      } else {
        // Check Supabase for saved progress
        const { data, error } = await supabase
          .from("course_progress")
          .select("completed_sections")
          .eq("user_id", userId)
          .eq("course_id", course.id)
          .single();

        if (data && !error && data.completed_sections) {
          // Convert JSON array to string array using type guard
          const completedSectionsArray = jsonStringArrayToStringArray(data.completed_sections);
          setCompletedSections(new Set(completedSectionsArray));
        }
      }
    } catch (error) {
      console.error("Error loading progress:", error);
    }
  }, [userId, course.id, course.slug]);

  const toggleSectionComplete = useCallback(
    async (sectionId: string) => {
      if (!userId) {
        toast({
          title: "Sign in required",
          description: "Please sign in to track your progress",
          variant: "destructive",
        });
        return;
      }

      const newCompleted = new Set(completedSections);
      if (newCompleted.has(sectionId)) {
        newCompleted.delete(sectionId);
      } else {
        newCompleted.add(sectionId);
      }

      setCompletedSections(newCompleted);

      try {
        const localStorageKey = createLocalStorageKey(course.slug, userId);
        const completedSectionsArray = Array.from(newCompleted);

        // Validate that all completed sections are strings
        const validCompletedSections = completedSectionsArray.filter(item => typeof item === 'string');

        const progressData = {
          completedSections: validCompletedSections,
          lastUpdated: new Date().toISOString(),
        };

        localStorage.setItem(localStorageKey, JSON.stringify(progressData));

        // Update Supabase
        const { error } = await supabase.from("course_progress").upsert({
          user_id: userId,
          course_id: course.id,
          course_title: course.title,
          course_slug: course.slug,
          category: "course", // Default category
          progress: calculateProgress(validCompletedSections.length, course.sections?.length || 0),
          completed_sections: validCompletedSections,
          last_activity: new Date().toISOString(),
        });

        if (error) {
          console.error("Error saving progress to Supabase:", error);
        }
      } catch (error) {
        console.error("Error saving progress:", error);
      }
    },
    [completedSections, userId, course.id, course.title, course.slug, course.sections],
  );

  return { completedSections, toggleSectionComplete, loadProgress };
}

// Parse content into sections
function parseContentIntoSections(
  content: string,
  sections: Section[],
): {
  sectionContents: Record<string, string>;
  subSections: SubSection[];
} {
  const sectionContents: Record<string, string> = {};
  const subSections: SubSection[] = [];

  if (!content || !sections) return { sectionContents, subSections };

  const lines = content.split("\n");
  let currentSectionContent: string[] = [];
  let currentSectionId: string | null = null;

  const findMatchingSection = (headerText: string) => {
    return sections.find((section) => {
      const sectionTitle = section.title.toLowerCase();
      const headerTitle = headerText.toLowerCase();

      // Try exact match first
      if (sectionTitle === headerTitle) return true;

      // Try partial matches
      if (
        sectionTitle.includes(headerTitle) ||
        headerTitle.includes(sectionTitle)
      )
        return true;

      // Try matching with section ID (convert dashes to spaces)
      const sectionIdAsTitle = section.id.replace(/-/g, " ").toLowerCase();
      return (
        sectionIdAsTitle === headerTitle ||
        headerTitle.includes(sectionIdAsTitle)
      );
    });
  };

  for (const line of lines) {
    if (line.startsWith("# ")) {
      // Save previous section
      if (currentSectionId && currentSectionContent.length > 0) {
        sectionContents[currentSectionId] = currentSectionContent
          .join("\n")
          .trim();
      }

      // Start new section
      const headerText = line.substring(2).trim();
      const matchingSection = findMatchingSection(headerText);

      if (matchingSection) {
        currentSectionId = matchingSection.id;
        currentSectionContent = [];
      } else {
        currentSectionContent.push(line);
      }
    } else if (line.startsWith("## ") && currentSectionId) {
      // Handle H2 subsections
      const subSectionTitle = line.substring(3).trim();
      const subSectionId = generateSubSectionId(
        currentSectionId,
        subSectionTitle,
      );

      subSections.push({
        id: subSectionId,
        title: subSectionTitle,
        sectionId: currentSectionId,
      });

      currentSectionContent.push(line);
    } else {
      currentSectionContent.push(line);
    }
  }

  // Save last section
  if (currentSectionId && currentSectionContent.length > 0) {
    sectionContents[currentSectionId] = currentSectionContent.join("\n").trim();
  }

  return { sectionContents, subSections };
}

export function SinglePageCourse({
  course,
  hasAccess,
  userId,
  showHeader = true,
}: SinglePageCourseProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [sectionContents, setSectionContents] = useState<
    Record<string, string>
  >({});
  const [subSections, setSubSections] = useState<SubSection[]>([]);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const { completedSections, toggleSectionComplete, loadProgress } =
    useProgressManagement(userId, course);
  const { t } = useTranslation();

  // Auto-expand all sections
  useEffect(() => {
    if (course.sections?.length > 0) {
      const sectionIds = course.sections.map((s) => s.id);
      setExpandedSections(new Set(sectionIds));
    }
  }, [course.sections]);

  // Parse content into sections
  useEffect(() => {
    if (course.singlePageContent && course.sections) {
      const {
        sectionContents: parsedSections,
        subSections: parsedSubSections,
      } = parseContentIntoSections(course.singlePageContent, course.sections);

      setSectionContents(parsedSections);
      setSubSections(parsedSubSections);
    }
  }, [course.singlePageContent, course.sections]);

  // Load progress on mount
  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(sectionId)) {
        newExpanded.delete(sectionId);
      } else {
        newExpanded.add(sectionId);
      }
      return newExpanded;
    });
  }, []);

  const toggleMobileNav = useCallback(() => {
    setIsMobileNavOpen((prev) => !prev);
  }, []);

  const closeMobileNav = useCallback(() => {
    setIsMobileNavOpen(false);
  }, []);

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      // Ensure section is expanded
      if (!expandedSections.has(sectionId)) {
        setExpandedSections((prev) => new Set(prev).add(sectionId));
      }
      // Scroll to section
      setTimeout(() => scrollToElement(sectionId), 100);
      // Close mobile navigation after navigation
      closeMobileNav();
    },
    [expandedSections, closeMobileNav],
  );

  const handleSubSectionClick = useCallback(
    (subSectionId: string, parentSectionId: string) => {
      // Ensure parent section is expanded
      if (!expandedSections.has(parentSectionId)) {
        setExpandedSections((prev) => new Set(prev).add(parentSectionId));
      }
      // Scroll to subsection
      setTimeout(() => scrollToElement(subSectionId), 100);
      // Close mobile navigation after navigation
      closeMobileNav();
    },
    [expandedSections, closeMobileNav],
  );

  if (!hasAccess) {
    return (
      <div className="container py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Required</h2>
        <p className="text-muted-foreground">
          You need to enroll in this course to access the content.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Sticky Mobile Navigation Toggle - Sliding Arrow Button */}
      {showHeader && (
        <div
          className={`fixed lg:hidden top-16 z-[9999] transition-all duration-300 ease-in-out ${
            isMobileNavOpen ? 'left-84' : 'left-4'
          }`}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMobileNav}
            className="bg-background/90 backdrop-blur-sm border-border hover:bg-accent hover:text-accent-foreground shadow-md transition-all duration-200"
          >
            {isMobileNavOpen ? (
              <ChevronLeft className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </Button>
        </div>
      )}

      {/* Header */}
      {showHeader && (
        <div className="text-center">
          <div className="flex items-center justify-center">
            <h1 className="text-3xl lg:text-4xl font-bold">{course.title}</h1>
          </div>
        </div>
      )}

      {/* Mobile Navigation Overlay */}
      {isMobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={closeMobileNav}
        >
          <div
            className="fixed inset-y-0 left-0 w-80 max-w-full bg-background shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">{t("courseContent")}</h3>
              <Button variant="ghost" size="sm" onClick={closeMobileNav}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(100vh-120px)]">
              {course.sections.map((section) => {
                const sectionSubSections = subSections.filter(
                  (sub) => sub.sectionId === section.id,
                );
                const hasSections = sectionSubSections.length > 0;
                const isSectionExpanded = expandedSections.has(section.id);

                return (
                  <div key={section.id} className="mb-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`flex-1 justify-start p-3 h-auto text-left rounded-lg ${"hover:bg-accent hover:text-accent-foreground"}`}
                        onClick={() => handleSectionClick(section.id)}
                      >
                        <span className="flex-1 text-sm font-medium truncate pr-2">
                          {section.title}
                        </span>
                        {completedSections.has(section.id) && (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        )}
                      </Button>

                      {hasSections && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2 h-auto rounded-lg hover:bg-accent"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSection(section.id);
                          }}
                        >
                          {isSectionExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>

                    {hasSections && isSectionExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {sectionSubSections.map((subSection) => (
                          <Button
                            key={subSection.id}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start p-2 h-auto text-left rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                            onClick={() =>
                              handleSubSectionClick(
                                subSection.id,
                                subSection.sectionId,
                              )
                            }
                          >
                            <span className="truncate text-xs">
                              {subSection.title}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Progress indicator */}
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground mb-2">
                  Progress: {completedSections.size} / {course.sections.length}{" "}
                  sections
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${calculateProgress(completedSections.size, course.sections.length)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Desktop Sidebar Navigation */}
        <div className="hidden lg:block lg:col-span-1">
          <Card className="p-4 lg:sticky lg:top-8">
            <h3 className="font-semibold mb-4">{t("courseContent")}</h3>
            <div className="overflow-y-auto max-h-[calc(100vh-200px)] pr-2">
              {course.sections.map((section) => {
                const sectionSubSections = subSections.filter(
                  (sub) => sub.sectionId === section.id,
                );
                const hasSections = sectionSubSections.length > 0;
                const isSectionExpanded = expandedSections.has(section.id);

                return (
                  <div key={section.id} className="mb-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`flex-1 justify-start p-3 h-auto text-left rounded-lg ${"hover:bg-accent hover:text-accent-foreground"}`}
                        onClick={() => handleSectionClick(section.id)}
                      >
                        <span className="flex-1 text-sm font-medium truncate pr-2">
                          {section.title}
                        </span>
                        {completedSections.has(section.id) && (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        )}
                      </Button>

                      {hasSections && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2 h-auto rounded-lg hover:bg-accent"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSection(section.id);
                          }}
                        >
                          {isSectionExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>

                    {hasSections && isSectionExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {sectionSubSections.map((subSection) => (
                          <Button
                            key={subSection.id}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start p-2 h-auto text-left rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                            onClick={() =>
                              handleSubSectionClick(
                                subSection.id,
                                subSection.sectionId,
                              )
                            }
                          >
                            <span className="truncate text-xs">
                              {subSection.title}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress indicator */}
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground mb-2">
                Progress: {completedSections.size} / {course.sections.length}{" "}
                sections
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${calculateProgress(completedSections.size, course.sections.length)}%`,
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="space-y-8">
            {course.sections.map((section) => {
              const isExpanded = expandedSections.has(section.id);
              const isCompleted = completedSections.has(section.id);

              return (
                <Card key={section.id} className="p-6" id={section.id}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleSection(section.id)}
                        className="p-2"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </Button>
                      <h2 className="text-3xl font-semibold">
                        {section.title}
                      </h2>
                      {isCompleted && (
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      )}
                    </div>

                    <Button
                      onClick={() => toggleSectionComplete(section.id)}
                      variant={isCompleted ? "secondary" : "default"}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <CheckCircle
                        className={`h-4 w-4 ${isCompleted ? "text-green-500" : ""}`}
                      />
                      {isCompleted ? "Mark as Incomplete" : "Mark as Complete"}
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="prose prose-lg max-w-none">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: addHeaderIds(
                            md.render(
                              sectionContents[section.id] ||
                                "Content loading...",
                            ),
                            section.id,
                          ),
                        }}
                      />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
